/**
 * Vera Role Catalog — single source of truth for Entra app-role names.
 *
 * These values must exactly match the `Value` field on the app roles
 * defined in the Vera Platform Entra app registration. The BFF extracts
 * them from the `roles` token claim and exposes them via the auth context.
 *
 * See docs/vera-platform-identity-setup.md for the Azure Portal setup.
 */

export const VERA_MODULES = [
  'ops',
  'catalog',
  'sales',
  'analytics',
  'finance',
  'implementation',
] as const;
export type VeraModule = (typeof VERA_MODULES)[number];

/** Role prefix per module. Matches the `Value` field on the Entra app role. */
export const ROLE_PREFIX: Record<VeraModule, string> = {
  ops: 'Ops',
  catalog: 'Catalog',
  sales: 'Sales',
  analytics: 'Analytics',
  finance: 'Finance',
  implementation: 'Implementation',
};

/** Global super-admin role — unlocks every module. */
export const GLOBAL_ADMIN_ROLE = 'Admin';

export interface VeraAppRole {
  value: string;
  displayName: string;
  description: string;
}

/**
 * All canonical role values that should be provisioned on the Vera Platform
 * app registration. When adding a new module, add its roles here and in the
 * Azure Portal (App Registration → App roles).
 */
export const VERA_APP_ROLES: readonly VeraAppRole[] = [
  { value: GLOBAL_ADMIN_ROLE, displayName: 'Vera Admin', description: 'Full admin access across all Vera modules' },
  { value: 'Ops.Admin', displayName: 'Operations Admin', description: 'Admin features in Vera Operations' },
  { value: 'Ops.Manager', displayName: 'Operations Manager', description: 'Manage records on behalf of any Ops user; no admin/config access' },
  { value: 'Ops.User', displayName: 'Operations User', description: 'Access to Vera Operations' },
  { value: 'Catalog.Admin', displayName: 'Catalog Admin', description: 'Admin features in Vera Catalog' },
  { value: 'Catalog.Manager', displayName: 'Catalog Manager', description: 'Manage records on behalf of any Catalog user; no admin/config access' },
  { value: 'Catalog.User', displayName: 'Catalog User', description: 'Access to Vera Catalog' },
  { value: 'Sales.Admin', displayName: 'Sales Admin', description: 'Admin features in Vera Sales' },
  { value: 'Sales.Manager', displayName: 'Sales Manager', description: 'Manage activities and pipeline on behalf of any Sales rep; no admin/config access' },
  { value: 'Sales.User', displayName: 'Sales User', description: 'Access to Vera Sales' },
] as const;

// ---------------------------------------------------------------------------
// Dataverse team sync
// ---------------------------------------------------------------------------

/**
 * Each Vera role maps to a plain Dataverse team. Granting a role
 * adds the user to that team so they inherit the team's security
 * role (actual data-layer access). Revoking removes them — unless
 * another role the user still holds maps to the same team.
 *
 * `<Prefix>.User` and `<Prefix>.Admin` intentionally map to the same
 * team: Dataverse-level data access is the same for both (the .Admin
 * distinction gates UI features, not data).
 *
 * The provision-vera-teams script creates these teams and attaches
 * the matching Dataverse security roles (Vera Admin, Vera Ops User,
 * Vera Catalog User, Vera Sales User — created separately via the
 * Power Apps maker portal).
 */
export const VERA_TEAM_NAMES = {
  ADMIN: 'Vera Admins Team',
  OPS: 'Vera Ops Team',
  CATALOG: 'Vera Catalog Team',
  SALES: 'Vera Sales Team',
} as const;

export type VeraTeamName = (typeof VERA_TEAM_NAMES)[keyof typeof VERA_TEAM_NAMES];

/**
 * Which Dataverse security role attaches to each team. The roles
 * already exist in the tenant (created during the legacy setup —
 * see docs/vera-entra-app-roles-setup.md).
 */
export const TEAM_TO_DATAVERSE_ROLE: Record<VeraTeamName, string> = {
  [VERA_TEAM_NAMES.ADMIN]: 'Vera Admin',
  [VERA_TEAM_NAMES.OPS]: 'Vera Ops User',
  [VERA_TEAM_NAMES.CATALOG]: 'Vera Catalog User',
  [VERA_TEAM_NAMES.SALES]: 'Vera Sales User',
};

/**
 * Role → team mapping. A role returns null if it doesn't need team sync
 * (reserved for future roles that are purely UI-level).
 */
export function teamForRole(roleValue: string): VeraTeamName | null {
  if (roleValue === GLOBAL_ADMIN_ROLE) return VERA_TEAM_NAMES.ADMIN;
  if (roleValue.startsWith('Ops.')) return VERA_TEAM_NAMES.OPS;
  if (roleValue.startsWith('Catalog.')) return VERA_TEAM_NAMES.CATALOG;
  if (roleValue.startsWith('Sales.')) return VERA_TEAM_NAMES.SALES;
  return null;
}

/**
 * All role values (from the catalog) that map to a given team. Used to
 * decide whether to remove a user from a team when a role is revoked —
 * only remove if the user holds no other role for the same team.
 */
export function rolesForTeam(team: VeraTeamName): string[] {
  return VERA_APP_ROLES.map((r) => r.value).filter((v) => teamForRole(v) === team);
}

const ALL_PREFIXES = Object.values(ROLE_PREFIX);

export function hasGlobalAdmin(roles: readonly string[]): boolean {
  return roles.includes(GLOBAL_ADMIN_ROLE);
}

export function isModuleAdmin(roles: readonly string[], rolePrefix: string): boolean {
  return hasGlobalAdmin(roles) || roles.includes(`${rolePrefix}.Admin`);
}

/**
 * True if the user can act on behalf of other users in the module — manager-or-admin tier.
 *
 * Use for features that let a privileged user enter, edit, or delete records
 * owned by other users (e.g., a sales manager logging activities on behalf of
 * a rep). Admin always implies manager. Manager does NOT imply admin —
 * configuration screens should still gate on `isModuleAdmin`.
 */
export function isModuleManager(roles: readonly string[], rolePrefix: string): boolean {
  return (
    hasGlobalAdmin(roles) ||
    roles.includes(`${rolePrefix}.Admin`) ||
    roles.includes(`${rolePrefix}.Manager`)
  );
}

export function hasModuleAccess(roles: readonly string[], rolePrefix: string): boolean {
  return (
    hasGlobalAdmin(roles) ||
    roles.some((r) => r.startsWith(`${rolePrefix}.`))
  );
}

/**
 * True if the user's token carries any Vera-shaped role claim.
 *
 * Used as a "Phase 0 fallback" gate: during the period before the Vera
 * Platform app registration is live, users authenticate through a legacy
 * per-app registration and have zero canonical Vera roles. The shell
 * treats that case as full access so developers aren't locked out.
 * Once Phase 1 is rolled out, every real user will have at least one
 * Vera role and the fallback is no longer reached.
 */
export function hasAnyVeraRole(roles: readonly string[]): boolean {
  if (hasGlobalAdmin(roles)) return true;
  return roles.some((r) => ALL_PREFIXES.some((p) => r.startsWith(`${p}.`)));
}
